/**
 * Support runtime upgrade standards (EOL-aware).
 *
 * Flag as "needs upgrade" when:
 * - major is older than any tracked release, or
 * - within `warnMonthsBeforeEol` of that release's EOL (default 6 months), or
 * - already past EOL
 *
 * Update `releases[].eol` / `targetMajor` when Microsoft/Node publish new calendars.
 */
export const RUNTIME_STANDARDS = {
  dotnet: {
    id: 'dotnet',
    label: '.NET',
    /** Recommended landing version for upgrades */
    targetMajor: 10,
    target: 'net10.0+',
    warnMonthsBeforeEol: 6,
    /**
     * Official support end dates (UTC date strings).
     * .NET 8 LTS → 2026-11-10; .NET 9 STS → 2026-05-12; .NET 10 LTS → ~2028-11-14
     */
    releases: [
      { major: 6, eol: '2024-11-12' },
      { major: 7, eol: '2024-05-14' },
      { major: 8, eol: '2026-11-10' },
      { major: 9, eol: '2026-05-12' },
      { major: 10, eol: '2028-11-14' }
    ],
    kinds: ['dotnet', 'net', 'csharp', 'c#', 'aspnet', 'asp.net']
  },
  node: {
    id: 'node',
    label: 'Node.js',
    targetMajor: 22,
    target: '22+',
    warnMonthsBeforeEol: 6,
    releases: [
      { major: 18, eol: '2025-04-30' },
      { major: 20, eol: '2026-04-30' },
      { major: 22, eol: '2027-04-30' },
      { major: 24, eol: '2028-04-30' }
    ],
    kinds: ['node', 'nodejs', 'javascript', 'typescript', 'js', 'ts']
  }
}

const STATUS_RANK = { behind: 3, unknown: 2, ok: 1 }

const DAY_MS = 24 * 60 * 60 * 1000

const parseEolDate = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(`${String(value).trim()}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return null
  return date
}

const addMonthsUtc = (date, months) => {
  const d = new Date(date.getTime())
  d.setUTCMonth(d.getUTCMonth() + months)
  return d
}

const formatEol = (date) => {
  if (!date) return ''
  return date.toISOString().slice(0, 10)
}

export const getReleasePolicy = (family, major) => {
  const std = RUNTIME_STANDARDS[family]
  if (!std) return null
  const release = (std.releases || []).find((r) => Number(r.major) === Number(major))
  if (!release) return null
  const eol = parseEolDate(release.eol)
  if (!eol) return null
  const warnMonths = Number(std.warnMonthsBeforeEol) || 6
  const warnFrom = addMonthsUtc(eol, -warnMonths)
  return { ...release, eol, warnFrom, warnMonths, target: std.target, targetMajor: std.targetMajor }
}

/**
 * Whether a major should be treated as needing an upgrade as of `now`.
 */
export const isMajorDueForUpgrade = (family, major, now = new Date()) => {
  const std = RUNTIME_STANDARDS[family]
  if (!std) return { due: false, reason: 'Unknown family' }

  if (Number(major) === 0) {
    return {
      due: true,
      reason: `${std.label} Framework is EOL / below ${std.target}`,
      target: std.target,
      phase: 'eol'
    }
  }

  const policy = getReleasePolicy(family, major)
  if (!policy) {
    if (Number(major) < Number(std.targetMajor)) {
      return {
        due: true,
        reason: `${std.label} ${major} is below current target ${std.target}`,
        target: std.target
      }
    }
    return {
      due: false,
      reason: `${std.label} ${major} has no EOL row; treated as current`,
      target: std.target
    }
  }

  const today = now instanceof Date ? now : new Date(now)
  if (today >= policy.eol) {
    return {
      due: true,
      reason: `${std.label} ${major} reached EOL ${formatEol(policy.eol)} — move to ${std.target}`,
      target: std.target,
      eol: policy.eol,
      phase: 'eol'
    }
  }

  if (today >= policy.warnFrom) {
    const daysLeft = Math.max(0, Math.ceil((policy.eol.getTime() - today.getTime()) / DAY_MS))
    return {
      due: true,
      reason: `${std.label} ${major} reaches EOL ${formatEol(policy.eol)} (in ~${daysLeft}d) — start moving to ${std.target}`,
      target: std.target,
      eol: policy.eol,
      warnFrom: policy.warnFrom,
      phase: 'warn',
      daysLeft
    }
  }

  return {
    due: false,
    reason: `${std.label} ${major} supported until ${formatEol(policy.eol)}`,
    target: std.target,
    eol: policy.eol,
    warnFrom: policy.warnFrom,
    phase: 'ok'
  }
}

export const describeRuntimeStandards = () => ({
  dotnet: `${RUNTIME_STANDARDS.dotnet.target} (warn ${RUNTIME_STANDARDS.dotnet.warnMonthsBeforeEol}mo before EOL)`,
  node: `${RUNTIME_STANDARDS.node.target} (warn ${RUNTIME_STANDARDS.node.warnMonthsBeforeEol}mo before EOL)`
})

/** Collapse ".NET 8.0", "NET 8", "net8.0-windows", catalog "dotnet8" into comparable tokens. */
export const normalizeRuntimeString = (runtime = '') => {
  let s = String(runtime || '').trim().toLowerCase()
  if (!s) return ''

  // .NETFramework,Version=v4.8 / Version=v4.7.2
  const fx = s.match(/net(?:framework)?[^\d]*version\s*=\s*v?(\d+)\.(\d+)/i) ||
    s.match(/net(?:framework)?[^\d]*v(\d+)\.(\d+)/i)
  if (fx) return `net${fx[1]}${fx[2]}`

  s = s.replace(/\.net\s*/g, 'net')
  s = s.replace(/\s+/g, '')

  // SOCR / TargetFrameworkParser catalog ids: dotnet8 → net8.0, dotnetcore31 → netcoreapp3.1
  const catalogDotnet = s.match(/^dotnet(\d+)$/i)
  if (catalogDotnet) return `net${catalogDotnet[1]}.0`
  const catalogCore = s.match(/^dotnetcore(\d)(\d)$/i)
  if (catalogCore) return `netcoreapp${catalogCore[1]}.${catalogCore[2]}`

  // net8.0-windows / net8.0-windows10.0.19041 → net8.0
  s = s.replace(/^(net(?:coreapp|standard)?\d+(?:\.\d+)?)[-+_].*$/, '$1')
  return s
}

/** Split multi-TFM strings: "net6.0;net8.0" */
export const splitRuntimeTokens = (runtime = '') => {
  const raw = String(runtime || '').trim()
  if (!raw) return []
  return raw
    .split(/[|;,/]+/)
    .map((part) => normalizeRuntimeString(part))
    .filter(Boolean)
}

export const detectRuntimeFamily = (runtime = '', kind = '') => {
  const k = String(kind || '').trim().toLowerCase()
  const tokens = splitRuntimeTokens(runtime)
  const r = tokens[0] || normalizeRuntimeString(runtime)

  if (RUNTIME_STANDARDS.dotnet.kinds.includes(k)) return 'dotnet'
  if (RUNTIME_STANDARDS.node.kinds.includes(k)) return 'node'

  if (
    /^dotnet\d/.test(r) ||
    /^net\d/.test(r) ||
    r.startsWith('netcore') ||
    r.startsWith('netstandard') ||
    r.startsWith('netframework') ||
    r.includes('netframework')
  ) return 'dotnet'

  if (/^node/.test(r) || /^v?\d+(\.\d+)*$/.test(r)) return 'node'

  return null
}

/** Parse major version for comparison; null if missing/unparseable. */
export const parseRuntimeMajor = (family, runtime) => {
  if (!runtime || !family) return null
  const r = normalizeRuntimeString(runtime)
  if (!r) return null

  if (family === 'dotnet') {
    // Classic .NET Framework TFMs (no decimal): net40, net45, net451, net452, net46,
    // net461, net462, net47, net471, net472, net48, net481 — all treated as below modern target.
    // Do NOT use /^net\d+/ here or net452 becomes "major 452" and looks newer than net10.
    if (
      /^net4\d{1,2}$/.test(r) ||
      /^net(?:framework)?4(?:\.\d+)+$/.test(r) ||
      /^netframework/.test(r) ||
      /^net[1-3]\d{0,2}$/.test(r)
    ) {
      return { major: 0, framework: true, token: r }
    }
    const modern = r.match(/^net(?:coreapp|standard)?(\d+)(?:\.(\d+))?/)
    if (modern) {
      return {
        major: Number(modern[1]),
        framework: false,
        token: r,
        // netstandard is a surface, not an app TFM — treat as informational if alone
        netstandard: r.startsWith('netstandard')
      }
    }
    return null
  }

  if (family === 'node') {
    const m = r.match(/^(?:node[_-]?|v)?(\d+)/)
    if (m) return { major: Number(m[1]), framework: false, token: r }
    return null
  }

  return null
}

const assessSingleToken = ({ token, kind, family, now = new Date() }) => {
  const std = RUNTIME_STANDARDS[family]
  const parsed = parseRuntimeMajor(family, token)
  if (!parsed) {
    return {
      status: 'unknown',
      family,
      runtime: token,
      kind: kind || '',
      label: std.label,
      target: std.target,
      reason: 'Unparseable version'
    }
  }

  // netstandard-* alone isn't an app runtime target for upgrade gating
  if (parsed.netstandard) {
    return {
      status: 'unknown',
      family,
      runtime: token,
      kind: kind || '',
      label: std.label,
      target: std.target,
      major: parsed.major,
      reason: `${token} is a library TFM (not an app runtime)`
    }
  }

  const due = isMajorDueForUpgrade(family, parsed.major, now)
  if (due.due) {
    return {
      status: 'behind',
      family,
      runtime: token,
      kind: kind || '',
      label: std.label,
      target: due.target || std.target,
      major: parsed.major,
      eol: due.eol || null,
      phase: due.phase || 'behind',
      reason: parsed.framework
        ? `${std.label} Framework ${token} is EOL / below ${std.target}`
        : due.reason
    }
  }

  return {
    status: 'ok',
    family,
    runtime: token,
    kind: kind || '',
    label: std.label,
    target: std.target,
    major: parsed.major,
    eol: due.eol || null,
    phase: due.phase || 'ok',
    reason: due.reason || `${std.label} ${token} meets ${std.target}`
  }
}

/**
 * Assess a runtime against standards.
 * Multi-TFM values use the best modern app TFM for overall status
 * (e.g. net6.0;net10.0 → ok because net10 is current; net6.0;net8.0 → behind while net8 is in EOL warn window).
 * No version / unrecognized → "unknown" (not needs-upgrade).
 */
export const assessRuntime = ({ runtime = '', kind = '', now = new Date() } = {}) => {
  const trimmed = typeof runtime === 'string' ? runtime.trim() : ''
  const family = detectRuntimeFamily(trimmed, kind)

  if (!trimmed) {
    return {
      status: 'unknown',
      family,
      runtime: '',
      kind: kind || '',
      reason: 'No runtime version'
    }
  }

  if (!family) {
    return {
      status: 'unknown',
      family: null,
      runtime: trimmed,
      kind: kind || '',
      reason: 'Unrecognized runtime family'
    }
  }

  const tokens = splitRuntimeTokens(trimmed)
  if (!tokens.length) {
    return {
      status: 'unknown',
      family,
      runtime: trimmed,
      kind: kind || '',
      label: RUNTIME_STANDARDS[family].label,
      target: RUNTIME_STANDARDS[family].target,
      reason: 'Unparseable version'
    }
  }

  const assessed = tokens.map((token) => assessSingleToken({ token, kind, family, now }))
  const actionable = assessed.filter((a) => a.status === 'ok' || a.status === 'behind')

  // Prefer meeting standard if any TFM does (multi-target projects)
  const bestOk = actionable
    .filter((a) => a.status === 'ok')
    .sort((a, b) => (b.major || 0) - (a.major || 0))[0]
  if (bestOk) {
    return {
      ...bestOk,
      runtime: trimmed,
      tokens: assessed
    }
  }

  const worstBehind = actionable
    .filter((a) => a.status === 'behind')
    .sort((a, b) => (a.major || 0) - (b.major || 0))[0]
  if (worstBehind) {
    return {
      ...worstBehind,
      runtime: trimmed,
      tokens: assessed
    }
  }

  return {
    ...assessed[0],
    runtime: trimmed,
    tokens: assessed
  }
}

const worseStatus = (a, b) => {
  const ra = STATUS_RANK[a] || 0
  const rb = STATUS_RANK[b] || 0
  return ra >= rb ? a : b
}

/** Roll up unique runtime chips for a client banner. */
export const summarizeClientRuntimes = (projectRuntimes = []) => {
  const byKey = new Map()

  ;(Array.isArray(projectRuntimes) ? projectRuntimes : []).forEach((rt) => {
    if (!rt || typeof rt !== 'object') return
    const assessment = assessRuntime(rt)
    const key = normalizeRuntimeString(assessment.runtime) ||
      assessment.runtime ||
      `(none:${assessment.kind || 'unknown'})`
    const prev = byKey.get(key)
    const chipRuntime = assessment.tokens?.find((t) => t.status === 'ok')?.runtime ||
      assessment.tokens?.find((t) => t.status === 'behind')?.runtime ||
      normalizeRuntimeString(assessment.runtime) ||
      assessment.runtime ||
      '—'

    if (!prev) {
      byKey.set(key, {
        runtime: chipRuntime,
        kind: assessment.kind,
        family: assessment.family,
        status: assessment.status,
        target: assessment.target || '',
        reason: assessment.reason
      })
      return
    }
    byKey.set(key, {
      ...prev,
      status: worseStatus(prev.status, assessment.status),
      reason: assessment.status === 'behind' ? assessment.reason : prev.reason
    })
  })

  return [...byKey.values()].sort((a, b) => {
    const statusDelta = (STATUS_RANK[b.status] || 0) - (STATUS_RANK[a.status] || 0)
    if (statusDelta !== 0) return statusDelta
    return String(a.runtime).localeCompare(String(b.runtime))
  })
}

/** Flat findings for console / detail lists (skips ok). */
export const collectUpgradeFindings = ({
  clientLabel = '',
  repositories = []
} = {}) => {
  const findings = []
  ;(Array.isArray(repositories) ? repositories : []).forEach((repo) => {
    ;(repo?.projectRuntimes || []).forEach((rt) => {
      const assessment = assessRuntime(rt)
      if (assessment.status === 'ok') return
      findings.push({
        client: clientLabel,
        repository: repo.repositoryName || repo.repositorySlug || '',
        projectName: rt.projectName || '',
        sourcePath: rt.sourcePath || '',
        runtime: assessment.runtime || '(none)',
        kind: assessment.kind || '',
        family: assessment.family || '',
        status: assessment.status,
        target: assessment.target || '',
        reason: assessment.reason
      })
    })
  })
  return findings
}

/**
 * Print a browser-console upgrade audit for the given clients.
 * Behind standards → warn; unknown/missing version → info (not treated as upgrade-needed).
 */
export const logRuntimeUpgradeReport = (clients = [], {
  title = 'Runtime upgrade check (.NET / Node)'
} = {}) => {
  const list = Array.isArray(clients) ? clients : []
  const behind = []
  const unknown = []

  list.forEach((client) => {
    const findings = collectUpgradeFindings({
      clientLabel: client.label,
      repositories: client.repositories
    })
    findings.forEach((f) => {
      if (f.status === 'behind') behind.push(f)
      else unknown.push(f)
    })
  })

  /* eslint-disable no-console */
  console.group(title)
  console.log('Standards:', describeRuntimeStandards())
  console.log(
    `${behind.length} need upgrade · ${unknown.length} unknown/no version · ${list.length} clients scanned`
  )

  if (behind.length) {
    console.groupCollapsed(`Needs upgrade (${behind.length})`)
    console.table(behind.map((f) => ({
      client: f.client,
      repo: f.repository,
      project: f.projectName,
      runtime: f.runtime,
      target: f.target,
      path: f.sourcePath
    })))
    behind.forEach((f) => {
      console.warn(
        `${f.client} · ${f.repository || '—'} · ${f.projectName || '—'} · ${f.runtime} → ${f.target}`,
        f.sourcePath || ''
      )
    })
    console.groupEnd()
  } else {
    console.log(`No runtimes in the EOL warn window (${describeRuntimeStandards().dotnet} / ${describeRuntimeStandards().node}).`)
  }

  if (unknown.length) {
    console.groupCollapsed(`No specific version / unrecognized (${unknown.length})`)
    console.table(unknown.map((f) => ({
      client: f.client,
      repo: f.repository,
      project: f.projectName,
      runtime: f.runtime,
      reason: f.reason,
      path: f.sourcePath
    })))
    console.groupEnd()
  }

  console.groupEnd()
  /* eslint-enable no-console */

  return { behind, unknown }
}
